import { useEffect } from 'react';
import { BRAND } from '../components/layout/Layout';

// SEO metadata configuration for each page
export const SEO_CONFIG = {
  home: {
    title: `${BRAND.name} | Leading IT Services & Software Development Company`,
    description: 'Plutops is a premier IT services company offering custom software development, web development, mobile app development, and IT consulting. Transform your business with expert solutions serving USA, UK, India, and worldwide.',
    keywords: 'IT services, software development company, web development services, app development company, IT consulting, custom software development, digital transformation, best software company, company to build my app, company to build my website',
  },
  services: {
    title: `IT Services & Software Development | ${BRAND.name}`,
    description: 'Explore our comprehensive IT services including custom software development, web application development, mobile app development, staff augmentation, AI consulting, and business automation solutions.',
    keywords: 'IT services, software development services, web development company, mobile app development, custom software, IT consulting services, staff augmentation, AI solutions',
  },
  about: {
    title: `About Us - Leading IT Company | ${BRAND.name}`,
    description: 'Learn about Plutops - a trusted IT services company with global presence in USA, UK, and India. 200+ projects delivered, 95% client retention. Expert software development and IT consulting.',
    keywords: 'about Plutops, IT company, software company, tech company, IT services provider, software development firm',
  },
  careers: {
    title: `Careers - Join Our Team | ${BRAND.name}`,
    description: 'Join Plutops - exciting career opportunities in software development, AI/ML, DevOps, and more. Work on impactful projects with global clients. Remote-friendly positions available.',
    keywords: 'IT jobs, software developer jobs, tech careers, remote IT jobs, developer positions, engineering jobs',
  },
  contact: {
    title: `Contact Us - Get a Free Consultation | ${BRAND.name}`,
    description: 'Contact Plutops for your software development needs. Get a free consultation. We serve clients in USA, UK, India, and worldwide. Custom software, web apps, mobile apps.',
    keywords: 'contact IT company, software development quote, IT consulting contact, hire developers',
  },
  blog: {
    title: `Tech Blog - Insights & Articles | ${BRAND.name}`,
    description: 'Read expert insights on software development, AI, digital transformation, and IT best practices from Plutops technical team.',
    keywords: 'tech blog, software development blog, IT insights, digital transformation articles, AI technology',
  },
  caseStudies: {
    title: `Case Studies - Success Stories | ${BRAND.name}`,
    description: 'Explore Plutops case studies showcasing successful software development projects, digital transformations, and IT solutions for Fortune 500 companies and startups.',
    keywords: 'IT case studies, software development success stories, digital transformation examples, client success',
  },
};

// Helper to update or create meta tag
const updateMetaTag = (name, content, isProperty = false) => {
  const attr = isProperty ? 'property' : 'name';
  let element = document.querySelector(`meta[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

// Helper to update or create link tag
const updateLinkTag = (rel, href) => {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

// Custom hook for SEO - React 19 compatible
export const useSEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  ogImage = '/og-image.png',
  type = 'website'
}) => {
  const siteUrl = 'https://plutops.com';
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  useEffect(() => {
    // Set document title
    if (title) {
      document.title = title;
    }

    // Primary Meta Tags
    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    updateMetaTag('title', title || '');

    // Canonical
    updateLinkTag('canonical', canonicalUrl);

    // Open Graph
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:title', title || '', true);
    updateMetaTag('og:description', description || '', true);
    updateMetaTag('og:image', fullOgImage, true);
    updateMetaTag('og:site_name', BRAND.name, true);

    // Twitter
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:url', canonicalUrl, true);
    updateMetaTag('twitter:title', title || '', true);
    updateMetaTag('twitter:description', description || '', true);
    updateMetaTag('twitter:image', fullOgImage, true);
  }, [title, description, keywords, canonicalUrl, type, fullOgImage]);
};

// Page SEO Component - React 19 compatible
export const PageSEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  ogImage = '/og-image.png',
  type = 'website'
}) => {
  const fullTitle = title || SEO_CONFIG.home.title;
  const fullDescription = description || SEO_CONFIG.home.description;
  const fullKeywords = keywords || SEO_CONFIG.home.keywords;

  useSEO({
    title: fullTitle,
    description: fullDescription,
    keywords: fullKeywords,
    canonical,
    ogImage,
    type
  });

  return null; // No DOM output needed
};

// Service Page SEO
export const ServicePageSEO = ({ service }) => {
  const title = `${service.title} Services | ${BRAND.name} - IT Solutions`;
  const description = `${service.description} Expert ${service.title.toLowerCase()} services from Plutops. Serving clients in USA, UK, India, and worldwide.`;
  const keywords = `${service.title.toLowerCase()}, ${service.title.toLowerCase()} services, ${service.title.toLowerCase()} company, IT services`;

  useSEO({
    title,
    description,
    keywords,
    canonical: `/services/${service.slug}`
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": BRAND.name,
      "url": "https://plutops.com"
    },
    "areaServed": ["US", "UK", "IN", "CA", "AU", "DE"],
    "serviceType": service.title
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Blog Article SEO
export const ArticleSEO = ({ article }) => {
  const title = `${article.title} | ${BRAND.name} Blog`;
  const description = article.excerpt;

  useSEO({
    title,
    description,
    keywords: article.tags?.join(', '),
    canonical: `/blog/${article.slug}`,
    type: 'article',
    ogImage: article.image_url
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image_url,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": BRAND.name,
      "logo": {
        "@type": "ImageObject",
        "url": "https://plutops.com/logo.png"
      }
    },
    "datePublished": article.published_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://plutops.com/blog/${article.slug}`
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// FAQ Schema Helper
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Breadcrumb Schema Helper
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://plutops.com${item.url}`
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default PageSEO;
